import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    ShoppingBag,
    Plus,
    Clock,
    CheckCircle,
    XCircle,
    ArrowLeft,
    Send,
    Trash2,
    ThumbsUp,
    ThumbsDown,
    User,
    Building,
    Package,
    Loader2,
    ClipboardList,
    Search,
    PlayCircle,
    CheckCircle2,
    MessageSquare,
    DollarSign,
    Paperclip,
    X,
    FileText,
    BarChart2,
    Hourglass
} from 'lucide-react';
import {
    PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid,
    Tooltip, Legend, ResponsiveContainer, AreaChart, Area
} from 'recharts';
import { useAuth } from '../context/AuthContext';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import axiosInstance from '../utils/axiosConfig';
import { normalizarTexto } from '../utils/texto';

const UNIDADES_MEDIDA = [
    'Unidad', 'Caja', 'Paquete', 'Resma', 'Galón', 'Litro',
    'Kilo', 'Metro', 'Rollo', 'Bolsa', 'Par', 'Juego', 'Otro'
];

const withAuthToken = (url) => {
    const token = localStorage.getItem('token');
    if (!token) return url;
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}token=${encodeURIComponent(token)}`;
};

// Badge de estado
const EstadoBadge = ({ estado }) => {
    const config = {
        pendiente: { label: 'Pendiente por aprobar', bg: 'bg-yellow-50 text-yellow-600 border-yellow-100', icon: Hourglass },
        aprobada: { label: 'En proceso', bg: 'bg-amber-50 text-amber-600 border-amber-100', icon: Clock },
        rechazada: { label: 'Rechazada', bg: 'bg-red-50 text-red-600 border-red-100', icon: XCircle },
        en_gestion: { label: 'En gestión', bg: 'bg-gray-100 text-gray-600 border-gray-200', icon: PlayCircle },
        completada: { label: 'Entregado', bg: 'bg-emerald-50 text-emerald-600 border-emerald-100', icon: CheckCircle2 },
        recibido: { label: 'Recibido', bg: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: CheckCircle }
    };
    const c = config[estado] || config.pendiente;
    const IconComp = c.icon;
    return (
        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium uppercase tracking-wider border ${c.bg}`}>
            <IconComp size={12} />
            {c.label}
        </span>
    );
};

const StatCard = ({ title, value, icon: Icon, color, onClick, active }) => (
    <div onClick={onClick} className={`bg-white p-6 rounded-3xl border ${active ? 'border-sura-blue shadow-md ring-2 ring-sura-blue/10' : 'border-gray-100 shadow-sm'} ${onClick ? 'cursor-pointer hover:shadow-md transition-all' : ''} flex items-center gap-4`}>
        <div className={`p-4 rounded-2xl ${color}`}>
            <Icon size={24} className="text-white" />
        </div>
        <div>
            <p className="text-sm text-gray-500 font-medium">{title}</p>
            <h3 className="text-2xl font-bold text-gray-900">{value}</h3>
        </div>
    </div>
);

const RequisicionComprasView = () => {
    const { user } = useAuth();
    const [searchParams] = useSearchParams();
    // El backend decide quien gestiona (rol procura, area de Procura u
    // observador especial) y lo manda en el usuario. El fallback cubre sesiones
    // viejas cuyo token se guardo antes de que existiera el campo.
    const isProcura = user?.ve_gestion_procura ?? (user?.role === 'procura');

    const initialTab = searchParams.get('tab');
    const [viewMode, setViewMode] = useState(
        initialTab === 'procura' ? 'procura' :
        initialTab === 'aprobar' ? 'list' :
        isProcura ? 'procura' : 'list'
    );
    const [scrollToApprovals, setScrollToApprovals] = useState(initialTab === 'aprobar');

    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    // Data
    const [misRequisiciones, setMisRequisiciones] = useState([]);
    const [porAprobar, setPorAprobar] = useState([]);
    const [bandejaProcura, setBandejaProcura] = useState([]);
    const [selectedRequisicion, setSelectedRequisicion] = useState(null);
    const [aprobadores, setAprobadores] = useState([]);
    const [solicitanteEsJefe, setSolicitanteEsJefe] = useState(false);
    const [aprobadorSearch, setAprobadorSearch] = useState('');

    // Modal rechazo
    const [showRechazoModal, setShowRechazoModal] = useState(false);
    const [motivoRechazo, setMotivoRechazo] = useState('');
    const [reqParaRechazar, setReqParaRechazar] = useState(null);

    // Modal completar
    const [showCompletarModal, setShowCompletarModal] = useState(false);
    const [notaProcura, setNotaProcura] = useState('');
    const [reqParaCompletar, setReqParaCompletar] = useState(null);

    // Seguimiento
    const [seguimientoTipo, setSeguimientoTipo] = useState('nota');
    const [seguimientoContenido, setSeguimientoContenido] = useState('');
    const [seguimientoProveedor, setSeguimientoProveedor] = useState('');
    const [seguimientoMonto, setSeguimientoMonto] = useState('');
    const [seguimientoArchivo, setSeguimientoArchivo] = useState(null);
    const [enviandoSeguimiento, setEnviandoSeguimiento] = useState(false);

    // Stats / Dashboard
    const [stats, setStats] = useState(null);
    const [statsLoading, setStatsLoading] = useState(false);
    const [rangoStats, setRangoStats] = useState('all');
    const [detalleGrafica, setDetalleGrafica] = useState(null);
    const [filterEstado, setFilterEstado] = useState('all');
    const [errorCarga, setErrorCarga] = useState(null);

    // Form
    const [formData, setFormData] = useState({
        aprobador_id: '',
        ultimo_proveedor: '',
        sugerencia_proveedor_1: '',
        sugerencia_proveedor_2: '',
        sugerencia_proveedor_3: '',
        observaciones: '',
        items: [{ cantidad: '', unidad_medida: 'Unidad', descripcion: '', marca_sugerida: '', plazo_recepcion: '' }]
    });

    // Fetch
    
    const fetchStats = useCallback(async () => {
        try {
            setStatsLoading(true);
            setErrorCarga(null);
            const params = new URLSearchParams();
            if (rangoStats !== 'all') params.append('rango', rangoStats);
            const res = await axiosInstance.get(`/api/requisiciones/stats?${params.toString()}`);
            setStats(res.data);
        } catch (err) {
            setErrorCarga(err.response?.data?.error || 'No se pudieron cargar las estadísticas.');
        } finally {
            setStatsLoading(false);
        }
    }, [rangoStats]);

    useEffect(() => {
        if (viewMode === 'stats') {
            fetchStats();
        }
    }, [viewMode, fetchStats]);


    const fetchRequisiciones = useCallback(async () => {
        try {
            setLoading(true);
            setErrorCarga(null);
            const res = await axiosInstance.get('/api/requisiciones/');
            setMisRequisiciones(res.data.mis_requisiciones || []);
            setPorAprobar(res.data.por_aprobar || []);
            setBandejaProcura(res.data.bandeja_procura || []);
        } catch (err) {
            setErrorCarga(err.response?.data?.error || 'No se pudieron cargar las requisiciones.');
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchAprobadores = useCallback(async () => {
        try {
            const res = await axiosInstance.get('/api/requisiciones/aprobadores');
            // Compat: el endpoint ahora devuelve { aprobadores, solicitante_es_jefe }
            const data = res.data || {};
            setAprobadores(Array.isArray(data) ? data : (data.aprobadores || []));
            setSolicitanteEsJefe(!!data.solicitante_es_jefe);
        } catch (err) {
            console.error('Error:', err);
        }
    }, []);

    useEffect(() => {
        fetchRequisiciones();
    }, [fetchRequisiciones]);

    useEffect(() => {
        if (viewMode === 'create' || viewMode === 'edit') fetchAprobadores();
    }, [viewMode, fetchAprobadores]);

    // Scroll to approvals section when coming from notification
    useEffect(() => {
        if (scrollToApprovals && !loading && porAprobar.length > 0) {
            setTimeout(() => {
                document.getElementById('por-aprobar-section')?.scrollIntoView({ behavior: 'smooth' });
                setScrollToApprovals(false);
            }, 300);
        }
    }, [scrollToApprovals, loading, porAprobar]);

    // Items handlers
    const addItem = () => {
        setFormData(prev => ({
            ...prev,
            items: [...prev.items, { cantidad: '', unidad_medida: 'Unidad', descripcion: '', marca_sugerida: '', plazo_recepcion: '' }]
        }));
    };

    const removeItem = (index) => {
        if (formData.items.length <= 1) return;
        setFormData(prev => ({
            ...prev,
            items: prev.items.filter((_, i) => i !== index)
        }));
    };

    const updateItem = (index, field, value) => {
        setFormData(prev => ({
            ...prev,
            items: prev.items.map((item, i) => i === index ? { ...item, [field]: value } : item)
        }));
    };

    const resetForm = () => {
        setFormData({
            aprobador_id: '',
            ultimo_proveedor: '',
            sugerencia_proveedor_1: '',
            sugerencia_proveedor_2: '',
            sugerencia_proveedor_3: '',
            observaciones: '',
            items: [{ cantidad: '', unidad_medida: 'Unidad', descripcion: '', marca_sugerida: '', plazo_recepcion: '' }]
        });
        setAprobadorSearch('');
    };

    // Crear
    const handleCreate = async (e) => {
        e.preventDefault();
        const finalSinAprobador = solicitanteEsJefe;

        if (!finalSinAprobador && !formData.aprobador_id) return toast.error('Debes seleccionar un aprobador (líder o gerente)');
        if (formData.items.some(i => !i.cantidad || !i.descripcion)) return toast.error('Todos los productos deben tener cantidad y descripción');

        setSubmitting(true);
        try {
            await axiosInstance.post('/api/requisiciones/', { ...formData, sin_aprobador: finalSinAprobador });
            toast.success('Requisición enviada');
            resetForm();
            setViewMode('list');
            fetchRequisiciones();
        } catch (err) {
            toast.error(err.response?.data?.error || 'Error al crear requisición');
        } finally {
            setSubmitting(false);
        }
    };

    // Editar (reenvío)
    const handleEdit = async (e) => {
        e.preventDefault();
        const finalSinAprobador = solicitanteEsJefe;
        if (!finalSinAprobador && !formData.aprobador_id) return toast.error('Debes seleccionar un aprobador (líder o gerente)');

        setSubmitting(true);
        try {
            await axiosInstance.put(`/api/requisiciones/${selectedRequisicion.id}`, { ...formData, sin_aprobador: finalSinAprobador });
            toast.success('Requisición reenviada');
            resetForm();
            setViewMode('list');
            fetchRequisiciones();
        } catch (err) {
            toast.error(err.response?.data?.error || 'Error al editar');
        } finally {
            setSubmitting(false);
        }
    };

    // Aprobar
    const handleAprobar = async (id) => {
        try {
            setSubmitting(true);
            await axiosInstance.post(`/api/requisiciones/${id}/aprobar`);
            toast.success('Requisición aprobada');
            fetchRequisiciones();
            if (selectedRequisicion?.id === id) {
                const res = await axiosInstance.get(`/api/requisiciones/${id}`);
                setSelectedRequisicion(res.data);
            }
        } catch (err) {
            toast.error(err.response?.data?.error || 'Error al aprobar');
        } finally {
            setSubmitting(false);
        }
    };

    // Confirmar recepción (solicitante)
    const handleConfirmarRecepcion = async (id) => {
        try {
            setSubmitting(true);
            await axiosInstance.post(`/api/requisiciones/${id}/confirmar-recepcion`);
            toast.success('Recepción confirmada');
            fetchRequisiciones();
            if (selectedRequisicion?.id === id) {
                const res = await axiosInstance.get(`/api/requisiciones/${id}`);
                setSelectedRequisicion(res.data);
            }
        } catch (err) {
            toast.error(err.response?.data?.error || 'Error al confirmar la recepción');
        } finally {
            setSubmitting(false);
        }
    };

    // Rechazar
    const handleRechazar = async () => {
        if (!motivoRechazo.trim()) return toast.error('Debe indicar un motivo');
        try {
            setSubmitting(true);
            await axiosInstance.post(`/api/requisiciones/${reqParaRechazar.id}/rechazar`, { motivo_rechazo: motivoRechazo });
            toast.success('Requisición rechazada');
            setShowRechazoModal(false);
            setMotivoRechazo('');
            setReqParaRechazar(null);
            fetchRequisiciones();
            if (selectedRequisicion?.id === reqParaRechazar.id) setViewMode('list');
        } catch (err) {
            toast.error(err.response?.data?.error || 'Error al rechazar');
        } finally {
            setSubmitting(false);
        }
    };

    // Tomar (Procura)
    const handleTomar = async (id) => {
        try {
            setSubmitting(true);
            const res = await axiosInstance.post(`/api/requisiciones/${id}/tomar`);
            setSelectedRequisicion(res.data);
            toast.success('Requisición tomada');
            fetchRequisiciones();
        } catch (err) {
            toast.error(err.response?.data?.error || 'Error');
        } finally {
            setSubmitting(false);
        }
    };

    // Completar (Procura)
    const handleCompletar = async () => {
        try {
            setSubmitting(true);
            await axiosInstance.post(`/api/requisiciones/${reqParaCompletar.id}/completar`, { nota_procura: notaProcura });
            toast.success('Requisición completada');
            setShowCompletarModal(false);
            setNotaProcura('');
            setReqParaCompletar(null);
            fetchRequisiciones();
            if (selectedRequisicion?.id === reqParaCompletar.id) setViewMode('list');
        } catch (err) {
            toast.error(err.response?.data?.error || 'Error');
        } finally {
            setSubmitting(false);
        }
    };

    // Seguimiento
    const handleCrearSeguimiento = async () => {
        if (!seguimientoContenido.trim() && !seguimientoArchivo) return;
        setEnviandoSeguimiento(true);
        try {
            const fd = new FormData();
            fd.append('tipo', seguimientoTipo);
            fd.append('contenido', seguimientoContenido);
            if (seguimientoTipo === 'cotizacion') {
                fd.append('proveedor', seguimientoProveedor);
                fd.append('monto', seguimientoMonto);
            }
            if (seguimientoArchivo) fd.append('archivo', seguimientoArchivo);

            await axiosInstance.post(`/api/requisiciones/${selectedRequisicion.id}/seguimiento`, fd);
            // Refresh detail
            const res = await axiosInstance.get(`/api/requisiciones/${selectedRequisicion.id}`);
            setSelectedRequisicion(res.data);
            toast.success('Seguimiento agregado');
            setSeguimientoContenido('');
            setSeguimientoProveedor('');
            setSeguimientoMonto('');
            setSeguimientoArchivo(null);
        } catch (err) {
            toast.error(err.response?.data?.error || 'Error');
        } finally {
            setEnviandoSeguimiento(false);
        }
    };

    const handleEliminarSeguimiento = async (segId) => {
        try {
            await axiosInstance.delete(`/api/requisiciones/${selectedRequisicion.id}/seguimiento/${segId}`);
            const res = await axiosInstance.get(`/api/requisiciones/${selectedRequisicion.id}`);
            setSelectedRequisicion(res.data);
            toast.success('Seguimiento eliminado');
        } catch (err) {
            toast.error('Error al eliminar');
        }
    };

    // Abrir detalle
    const openDetail = (req) => {
        setSelectedRequisicion(req);
        setViewMode('detail');
    };

    // Abrir edición (reenvío)
    const openEdit = (req) => {
        setSelectedRequisicion(req);
        setFormData({
            aprobador_id: req.aprobador_id || '',
            ultimo_proveedor: req.ultimo_proveedor || '',
            sugerencia_proveedor_1: req.sugerencia_proveedor_1 || '',
            sugerencia_proveedor_2: req.sugerencia_proveedor_2 || '',
            sugerencia_proveedor_3: req.sugerencia_proveedor_3 || '',
            observaciones: req.observaciones || '',
            items: req.items.length > 0 ? req.items.map(i => ({
                cantidad: i.cantidad,
                unidad_medida: i.unidad_medida,
                descripcion: i.descripcion,
                marca_sugerida: i.marca_sugerida || '',
                plazo_recepcion: i.plazo_recepcion || ''
            })) : [{ cantidad: '', unidad_medida: 'Unidad', descripcion: '', marca_sugerida: '', plazo_recepcion: '' }]
        });
        setViewMode('edit');
    };

    // Stats
    const totalPendiente = misRequisiciones.filter(r => r.estado === 'pendiente').length;
    const totalEnProceso = misRequisiciones.filter(r => r.estado === 'aprobada').length;
    const totalEnGestion = misRequisiciones.filter(r => r.estado === 'en_gestion').length;
    const totalCompletadas = misRequisiciones.filter(r => ['completada', 'recibido'].includes(r.estado)).length;

    // Filtered aprobadores
    const aprobadorQuery = normalizarTexto(aprobadorSearch).trim();
    const filteredAprobadores = aprobadores.filter(a =>
        normalizarTexto(a.nombre).includes(aprobadorQuery) ||
        normalizarTexto(a.cargo).includes(aprobadorQuery) ||
        normalizarTexto(a.departamento).includes(aprobadorQuery)
    );

    // ============================================
    // RENDER: FORMULARIO
    // ============================================
    const renderForm = (isEdit = false) => (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
            <div className="flex items-center gap-3 mb-6">
                <button onClick={() => { resetForm(); setViewMode('list'); }} className="p-2 hover:bg-gray-100 rounded-xl transition-colors">
                    <ArrowLeft size={20} />
                </button>
                <h2 className="text-xl font-bold text-gray-900">{isEdit ? 'Editar y reenviar requisición' : 'Nueva Requisición de Compras'}</h2>
            </div>

            <form onSubmit={isEdit ? handleEdit : handleCreate} className="space-y-6">
                {/* Aprobador */}
                {solicitanteEsJefe ? (
                    <div className="bg-sura-blue/5 border border-sura-blue/20 rounded-2xl p-4 flex items-start gap-2">
                        <CheckCircle size={16} className="text-sura-blue mt-0.5 shrink-0" />
                        <span className="text-sm text-sura-blue-dark">Como líder/gerente, tu requisición no requiere aprobación: pasa directo a Procura.</span>
                    </div>
                ) : (
                    <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
                        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Aprobador</h3>
                        <div>
                            <label className="block text-sm text-gray-600 mb-1">Selecciona el líder o gerente que aprueba esta requisición</label>
                            <div className="relative">
                                <Search size={16} className="absolute left-3 top-3 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Buscar por nombre, cargo o departamento..."
                                    value={aprobadorSearch}
                                    onChange={e => setAprobadorSearch(e.target.value)}
                                    className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sura-blue/20 focus:border-sura-blue/40"
                                />
                            </div>
                            {aprobadorSearch && (
                                <div className="mt-2 max-h-48 overflow-y-auto border border-gray-100 rounded-xl divide-y divide-gray-50">
                                    {filteredAprobadores.slice(0, 10).map(a => (
                                        <button
                                            key={a.id}
                                            type="button"
                                            onClick={() => {
                                                setFormData(prev => ({ ...prev, aprobador_id: a.id }));
                                                setAprobadorSearch(a.nombre);
                                            }}
                                            className={`w-full text-left px-4 py-3 hover:bg-sura-blue/5 transition-colors ${formData.aprobador_id === a.id ? 'bg-sura-blue/5' : ''}`}
                                        >
                                            <p className="text-sm font-medium text-gray-900">{a.nombre}</p>
                                            <p className="text-xs text-gray-500">{a.cargo} — {a.departamento}</p>
                                        </button>
                                    ))}
                                    {filteredAprobadores.length === 0 && (
                                        <p className="px-4 py-3 text-sm text-gray-400">No se encontraron resultados</p>
                                    )}
                                </div>
                            )}
                            {formData.aprobador_id && !aprobadorSearch && (
                                <p className="mt-2 text-sm text-sura-blue">
                                    Aprobador seleccionado: {aprobadores.find(a => a.id === formData.aprobador_id)?.nombre}
                                </p>
                            )}
                        </div>
                    </div>
                )}

                {/* Productos */}
                <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
                    <div className="flex items-center justify-between">
                        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Productos</h3>
                        <button type="button" onClick={addItem} className="text-sm text-sura-blue hover:text-sura-blue-dark font-medium flex items-center gap-1">
                            <Plus size={14} /> Agregar producto
                        </button>
                    </div>

                    {formData.items.map((item, idx) => (
                        <div key={idx} className="border border-gray-100 rounded-xl p-4 space-y-3">
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-medium text-gray-400">Producto {idx + 1}</span>
                                {formData.items.length > 1 && (
                                    <button type="button" onClick={() => removeItem(idx)} className="text-red-400 hover:text-red-600">
                                        <Trash2 size={14} />
                                    </button>
                                )}
                            </div>
                            <div className="grid grid-cols-12 gap-3">
                                <div className="col-span-2">
                                    <label className="block text-xs text-gray-500 mb-1">Cantidad *</label>
                                    <input type="number" min="0" step="any" value={item.cantidad}
                                        onChange={e => updateItem(idx, 'cantidad', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sura-blue/20" required />
                                </div>
                                <div className="col-span-2">
                                    <label className="block text-xs text-gray-500 mb-1">Unidad *</label>
                                    <select value={item.unidad_medida} onChange={e => updateItem(idx, 'unidad_medida', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sura-blue/20">
                                        {UNIDADES_MEDIDA.map(u => <option key={u} value={u}>{u}</option>)}
                                    </select>
                                </div>
                                <div className="col-span-4">
                                    <label className="block text-xs text-gray-500 mb-1">Descripción *</label>
                                    <input type="text" value={item.descripcion}
                                        onChange={e => updateItem(idx, 'descripcion', e.target.value)}
                                        placeholder="Descripción completa del producto"
                                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sura-blue/20" required />
                                </div>
                                <div className="col-span-2">
                                    <label className="block text-xs text-gray-500 mb-1">Marca sugerida</label>
                                    <input type="text" value={item.marca_sugerida}
                                        onChange={e => updateItem(idx, 'marca_sugerida', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sura-blue/20" />
                                </div>
                                <div className="col-span-2">
                                    <label className="block text-xs text-gray-500 mb-1">Plazo recepción</label>
                                    <input type="text" value={item.plazo_recepcion}
                                        onChange={e => updateItem(idx, 'plazo_recepcion', e.target.value)}
                                        placeholder="ej: 1 semana"
                                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sura-blue/20" />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="flex justify-end">
                    <button type="submit" disabled={submitting}
                        className="flex items-center gap-2 px-6 py-3 bg-sura-blue text-white rounded-xl font-medium hover:bg-sura-blue-dark transition-colors disabled:opacity-50">
                        {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                        {isEdit ? 'Reenviar requisición' : 'Enviar requisición'}
                    </button>
                </div>
            </form>
        </motion.div>
    );

    // ============================================
    // RENDER: SEGUIMIENTO (panel en detalle)
    // ============================================
    const renderSeguimiento = () => {
        const req = selectedRequisicion;
        if (!req || !['en_gestion', 'aprobada'].includes(req.estado)) return null;
        const canEdit = isProcura;

        return (
            <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Seguimiento de gestión</h3>

                {/* Lista de seguimientos */}
                {req.seguimientos && req.seguimientos.length > 0 && (
                    <div className="space-y-3">
                        {req.seguimientos.map(seg => (
                            <div key={seg.id} className={`p-4 rounded-xl border ${seg.tipo === 'cotizacion' ? 'border-green-100 bg-green-50/50' : seg.tipo === 'archivo' ? 'border-sura-blue/20 bg-sura-blue/5' : 'border-gray-100 bg-gray-50/50'}`}>
                                <div className="flex items-start justify-between">
                                    <div className="flex items-center gap-2 mb-1">
                                        {seg.tipo === 'nota' && <MessageSquare size={14} className="text-gray-500" />}
                                        {seg.tipo === 'cotizacion' && <DollarSign size={14} className="text-green-600" />}
                                        {seg.tipo === 'archivo' && <Paperclip size={14} className="text-sura-blue" />}
                                        <span className="text-xs font-medium text-gray-500">{seg.usuario_nombre} — {new Date(seg.created_at).toLocaleString('es-VE')}</span>
                                    </div>
                                    {canEdit && (
                                        <button
                                            type="button"
                                            onClick={() => handleEliminarSeguimiento(seg.id)}
                                            className="text-gray-300 hover:text-red-500 transition-colors"
                                            aria-label="Eliminar seguimiento"
                                        >
                                            <X size={14} />
                                        </button>
                                    )}
                                </div>
                                {seg.contenido && <p className="text-sm text-gray-700">{seg.contenido}</p>}
                                {seg.tipo === 'cotizacion' && (
                                    <div className="mt-2 flex items-center gap-4 text-sm">
                                        {seg.proveedor && <span className="text-green-700 font-medium">{seg.proveedor}</span>}
                                        {seg.monto && <span className="text-green-800 font-bold">${seg.monto.toLocaleString('es-VE', { minimumFractionDigits: 2 })}</span>}
                                    </div>
                                )}
                                {seg.archivo && (
                                    <a href={withAuthToken(`/api/requisiciones/${req.id}/seguimiento/${seg.id}/archivo`)} target="_blank" rel="noopener noreferrer"
                                        className="mt-2 inline-flex items-center gap-1 text-sm text-sura-blue hover:text-sura-blue-dark">
                                        <FileText size={14} /> Ver archivo
                                    </a>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {/* Comparación de cotizaciones */}
                {req.seguimientos && req.seguimientos.filter(s => s.tipo === 'cotizacion').length > 1 && (
                    <div className="p-4 bg-amber-50 border border-amber-100 rounded-xl">
                        <h4 className="text-xs font-semibold text-amber-700 uppercase mb-2">Comparación de cotizaciones</h4>
                        <div className="space-y-1">
                            {req.seguimientos.filter(s => s.tipo === 'cotizacion').sort((a, b) => (a.monto || 0) - (b.monto || 0)).map((s, i) => (
                                <div key={s.id} className={`flex items-center justify-between text-sm py-1 ${i === 0 ? 'text-green-700 font-bold' : 'text-gray-600'}`}>
                                    <span>{s.proveedor || 'Sin nombre'}</span>
                                    <span>${(s.monto || 0).toLocaleString('es-VE', { minimumFractionDigits: 2 })}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Formulario nuevo seguimiento */}
                {canEdit && (
                    <div className="border-t border-gray-100 pt-4 space-y-3">
                        <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit">
                            {[
                                { key: 'nota', label: 'Nota', icon: MessageSquare },
                                { key: 'cotizacion', label: 'Cotización', icon: DollarSign },
                                { key: 'archivo', label: 'Archivo', icon: Paperclip },
                            ].map(t => (
                                <button key={t.key} type="button" onClick={() => setSeguimientoTipo(t.key)}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${seguimientoTipo === t.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                                    <t.icon size={12} /> {t.label}
                                </button>
                            ))}
                        </div>

                        {seguimientoTipo === 'cotizacion' && (
                            <div className="grid grid-cols-2 gap-3">
                                <input type="text" name="proveedor" autoComplete="off" aria-label="Nombre del proveedor" placeholder="Nombre del proveedor"
                                    value={seguimientoProveedor} onChange={e => setSeguimientoProveedor(e.target.value)}
                                    className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sura-blue/20" />
                                <input type="number" name="monto" inputMode="decimal" step="0.01" aria-label="Monto" placeholder="Monto ($)"
                                    value={seguimientoMonto} onChange={e => setSeguimientoMonto(e.target.value)}
                                    className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sura-blue/20" />
                            </div>
                        )}

                        <textarea name="seguimiento_contenido" aria-label="Contenido del seguimiento" autoComplete="off" placeholder={seguimientoTipo === 'cotizacion' ? 'Detalles de la cotización…' : seguimientoTipo === 'archivo' ? 'Descripción del archivo…' : 'Escribir nota…'}
                            value={seguimientoContenido} onChange={e => setSeguimientoContenido(e.target.value)}
                            rows={2} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sura-blue/20 resize-none" />

                        {(seguimientoTipo === 'archivo' || seguimientoTipo === 'cotizacion') && (
                            <div>
                                <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-500 hover:text-sura-blue">
                                    <Paperclip size={14} />
                                    {seguimientoArchivo ? seguimientoArchivo.name : 'Adjuntar archivo'}
                                    <input type="file" className="hidden" onChange={e => setSeguimientoArchivo(e.target.files[0])} />
                                </label>
                            </div>
                        )}

                        <div className="flex justify-end">
                            <button onClick={handleCrearSeguimiento} disabled={enviandoSeguimiento || (!seguimientoContenido.trim() && !seguimientoArchivo)}
                                className="flex items-center gap-2 px-4 py-2 bg-sura-blue text-white rounded-lg text-sm font-medium hover:bg-sura-blue-dark transition-colors disabled:opacity-50">
                                {enviandoSeguimiento ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                                Agregar
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // ============================================
    // RENDER: DETALLE
    // ============================================
    const renderDetail = () => {
        if (!selectedRequisicion) return null;
        const req = selectedRequisicion;
        const canApprove = req.aprobador_id === user?.empleado_id && req.estado === 'pendiente';
        const canProcura = isProcura && ['aprobada', 'en_gestion'].includes(req.estado);

        return (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
                <div className="flex items-center gap-3 mb-6">
                    <button onClick={() => setViewMode(isProcura ? 'procura' : 'list')} className="p-2 hover:bg-gray-100 rounded-xl transition-colors">
                        <ArrowLeft size={20} />
                    </button>
                    <div className="flex-1">
                        <h2 className="text-xl font-bold text-gray-900">{req.codigo}</h2>
                        <p className="text-sm text-gray-500">Creada el {new Date(req.created_at).toLocaleDateString('es-VE')}</p>
                    </div>
                    <EstadoBadge estado={req.estado} />
                </div>

                {/* Info general */}
                <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
                    <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Información general</h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                        <div className="flex items-center gap-2">
                            <User size={14} className="text-gray-400" />
                            <span className="text-gray-500">Solicitante:</span>
                            <span className="font-medium">{req.empleado_nombre}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Building size={14} className="text-gray-400" />
                            <span className="text-gray-500">Departamento:</span>
                            <span className="font-medium">{req.departamento_solicitante}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-gray-500">Centro de costo:</span>
                            <span className="font-medium">{req.centro_costo}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-gray-500">Sede:</span>
                            <span className="font-medium">{req.sede_nombre}</span>
                        </div>
                        {req.aprobador_nombre && (
                            <div className="flex items-center gap-2">
                                <span className="text-gray-500">Aprobador:</span>
                                <span className="font-medium">{req.aprobador_nombre}</span>
                            </div>
                        )}
                        {!req.aprobador_id && (
                            <div className="flex items-center gap-2">
                                <span className="text-gray-500">Aprobación:</span>
                                <span className="font-medium text-sura-blue">Sin aprobador requerido</span>
                            </div>
                        )}
                        {req.gestionado_por && (
                            <div className="flex items-center gap-2">
                                <span className="text-gray-500">Gestionado por:</span>
                                <span className="font-medium">{req.gestionado_por}</span>
                            </div>
                        )}
                    </div>

                    {req.motivo_rechazo && (
                        <div className="mt-4 p-3 bg-red-50 border border-red-100 rounded-xl">
                            <p className="text-sm text-red-700"><strong>Motivo de rechazo:</strong> {req.motivo_rechazo}</p>
                        </div>
                    )}

                    {req.nota_procura && (
                        <div className="mt-4 p-3 bg-emerald-50 border border-emerald-100 rounded-xl">
                            <p className="text-sm text-emerald-700"><strong>Nota de Procura:</strong> {req.nota_procura}</p>
                        </div>
                    )}
                </div>

                {/* Items */}
                <div className="bg-white rounded-2xl border border-gray-100 p-6">
                    <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">Productos ({req.items.length})</h3>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-gray-100">
                                    <th className="text-left py-2 px-3 text-xs text-gray-500 font-medium">Cant.</th>
                                    <th className="text-left py-2 px-3 text-xs text-gray-500 font-medium">Unidad</th>
                                    <th className="text-left py-2 px-3 text-xs text-gray-500 font-medium">Descripción</th>
                                    <th className="text-left py-2 px-3 text-xs text-gray-500 font-medium">Marca sugerida</th>
                                    <th className="text-left py-2 px-3 text-xs text-gray-500 font-medium">Plazo</th>
                                </tr>
                            </thead>
                            <tbody>
                                {req.items.map((item, idx) => (
                                    <tr key={idx} className="border-b border-gray-50">
                                        <td className="py-2.5 px-3 font-medium">{item.cantidad}</td>
                                        <td className="py-2.5 px-3">{item.unidad_medida}</td>
                                        <td className="py-2.5 px-3">{item.descripcion}</td>
                                        <td className="py-2.5 px-3 text-gray-500">{item.marca_sugerida || '—'}</td>
                                        <td className="py-2.5 px-3 text-gray-500">{item.plazo_recepcion || '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Proveedores */}
                {(req.ultimo_proveedor || req.sugerencia_proveedor_1 || req.observaciones) && (
                    <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-3">
                        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Proveedores y observaciones</h3>
                        {req.ultimo_proveedor && <p className="text-sm"><span className="text-gray-500">Último proveedor:</span> {req.ultimo_proveedor}</p>}
                        {req.sugerencia_proveedor_1 && <p className="text-sm"><span className="text-gray-500">Sugerencia 1:</span> {req.sugerencia_proveedor_1}</p>}
                        {req.sugerencia_proveedor_2 && <p className="text-sm"><span className="text-gray-500">Sugerencia 2:</span> {req.sugerencia_proveedor_2}</p>}
                        {req.sugerencia_proveedor_3 && <p className="text-sm"><span className="text-gray-500">Sugerencia 3:</span> {req.sugerencia_proveedor_3}</p>}
                        {req.observaciones && <p className="text-sm"><span className="text-gray-500">Observaciones:</span> {req.observaciones}</p>}
                    </div>
                )}

                {/* Seguimiento Procura */}
                {renderSeguimiento()}

                {/* Acciones */}
                <div className="flex gap-3">
                    {canApprove && (
                        <>
                            <button onClick={() => handleAprobar(req.id)} disabled={submitting}
                                className="flex items-center gap-2 px-5 py-2.5 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 transition-colors disabled:opacity-50">
                                <ThumbsUp size={16} /> Aprobar
                            </button>
                            <button onClick={() => { setReqParaRechazar(req); setShowRechazoModal(true); }}
                                className="flex items-center gap-2 px-5 py-2.5 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 transition-colors">
                                <ThumbsDown size={16} /> Rechazar
                            </button>
                        </>
                    )}
                    {canProcura && req.estado === 'aprobada' && (
                        <button onClick={() => handleTomar(req.id)} disabled={submitting}
                            className="flex items-center gap-2 px-5 py-2.5 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700 transition-colors disabled:opacity-50">
                            <PlayCircle size={16} /> Tomar requisición
                        </button>
                    )}
                    {canProcura && req.estado === 'en_gestion' && (
                        <>
                            <button onClick={() => { setReqParaCompletar(req); setShowCompletarModal(true); }} disabled={submitting}
                                className="flex items-center gap-2 px-5 py-2.5 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 transition-colors disabled:opacity-50">
                                <CheckCircle2 size={16} /> Marcar completada
                            </button>
                            <button onClick={() => { setReqParaRechazar(req); setShowRechazoModal(true); }}
                                className="flex items-center gap-2 px-5 py-2.5 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 transition-colors">
                                <ThumbsDown size={16} /> Rechazar
                            </button>
                        </>
                    )}
                    {req.estado === 'rechazada' && req.empleado_id === user?.empleado_id && (
                        <button onClick={() => openEdit(req)}
                            className="flex items-center gap-2 px-5 py-2.5 bg-sura-blue text-white rounded-xl font-medium hover:bg-sura-blue-dark transition-colors">
                            <Send size={16} /> Editar y reenviar
                        </button>
                    )}
                    {req.estado === 'completada' && req.empleado_id === user?.empleado_id && (
                        <button onClick={() => handleConfirmarRecepcion(req.id)} disabled={submitting}
                            className="flex items-center gap-2 px-5 py-2.5 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 transition-colors disabled:opacity-50">
                            <CheckCircle2 size={16} /> Confirmar recepción
                        </button>
                    )}
                </div>
            </motion.div>
        );
    };

    // ============================================
    // RENDER: CARD
    // ============================================
    const renderRequisicionCard = (req) => (
        <motion.div key={req.id} layout initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl border border-gray-100 p-5 hover:shadow-md transition-shadow cursor-pointer"
            onClick={() => openDetail(req)}>
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                    <div className="p-2 rounded-xl bg-sura-blue/5">
                        <ShoppingBag size={18} className="text-sura-blue" />
                    </div>
                    <div>
                        <h4 className="font-semibold text-gray-900">{req.codigo}</h4>
                        <p className="text-xs text-gray-500">{new Date(req.created_at).toLocaleDateString('es-VE')}</p>
                    </div>
                </div>
                <EstadoBadge estado={req.estado} />
            </div>
            <div className="flex items-center gap-4 text-sm text-gray-500">
                <span className="flex items-center gap-1"><User size={13} /> {req.empleado_nombre}</span>
                <span className="flex items-center gap-1"><Package size={13} /> {req.items.length} producto{req.items.length !== 1 ? 's' : ''}</span>
                {req.departamento_solicitante && <span className="flex items-center gap-1"><Building size={13} /> {req.departamento_solicitante}</span>}
            </div>
        </motion.div>
    );

    // ============================================
    // RENDER: LISTA
    // ============================================
    const filteredMisRequisiciones = misRequisiciones.filter(r => {
        if (filterEstado === 'all') return true;
        if (filterEstado === 'pendiente') return r.estado === 'pendiente';
        if (filterEstado === 'en_proceso') return r.estado === 'aprobada';
        if (filterEstado === 'en_gestion') return r.estado === 'en_gestion';
        if (filterEstado === 'completada') return ['completada', 'recibido'].includes(r.estado);
        return true;
    });

    const renderList = () => (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <StatCard title="Total" value={misRequisiciones.length} icon={ClipboardList} color="bg-sura-blue" onClick={() => setFilterEstado('all')} active={filterEstado === 'all'} />
                <StatCard title="Pendiente por aprobar" value={totalPendiente} icon={Hourglass} color="bg-yellow-500" onClick={() => setFilterEstado('pendiente')} active={filterEstado === 'pendiente'} />
                <StatCard title="En proceso" value={totalEnProceso} icon={Clock} color="bg-amber-500" onClick={() => setFilterEstado('en_proceso')} active={filterEstado === 'en_proceso'} />
                <StatCard title="En gestión" value={totalEnGestion} icon={PlayCircle} color="bg-gray-500" onClick={() => setFilterEstado('en_gestion')} active={filterEstado === 'en_gestion'} />
                <StatCard title="Entregadas" value={totalCompletadas} icon={CheckCircle2} color="bg-emerald-500" onClick={() => setFilterEstado('completada')} active={filterEstado === 'completada'} />
            </div>

            <div className="flex justify-between items-center">
                <h2 className="text-lg font-bold text-gray-900">Mis requisiciones {filterEstado !== 'all' && <span className="text-sm font-normal text-sura-blue ml-2">(Filtrado)</span>}</h2>
                <button onClick={() => setViewMode('create')}
                    className="flex items-center gap-2 px-4 py-2.5 bg-sura-blue text-white rounded-xl font-medium hover:bg-sura-blue-dark transition-colors text-sm">
                    <Plus size={16} /> Nueva requisición
                </button>
            </div>

            {loading ? (
                <div className="flex justify-center py-12"><Loader2 size={24} className="animate-spin text-gray-400" /></div>
            ) : filteredMisRequisiciones.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                    <ShoppingBag size={48} className="mx-auto mb-3 opacity-30" />
                    <p>No se encontraron requisiciones</p>
                </div>
            ) : (
                <div className="space-y-3">{filteredMisRequisiciones.map(renderRequisicionCard)}</div>
            )}

            {porAprobar.length > 0 && (
                <div id="por-aprobar-section" className="space-y-3 mt-8">
                    <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                        <ThumbsUp size={18} className="text-amber-500" />
                        Pendientes de mi aprobación ({porAprobar.length})
                    </h2>
                    {porAprobar.map(renderRequisicionCard)}
                </div>
            )}
        </motion.div>
    );

    // ============================================
    // RENDER: BANDEJA PROCURA
    // ============================================
    const filteredBandejaProcura = bandejaProcura.filter(r => {
        if (filterEstado === 'all') return true;
        if (filterEstado === 'pendiente') return r.estado === 'pendiente';
        if (filterEstado === 'en_proceso') return r.estado === 'aprobada';
        if (filterEstado === 'en_gestion') return r.estado === 'en_gestion';
        if (filterEstado === 'completada') return ['completada', 'recibido'].includes(r.estado);
        return true;
    });

    const renderProcura = () => (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <StatCard title="Total" value={bandejaProcura.length} icon={ClipboardList} color="bg-sura-blue" onClick={() => setFilterEstado('all')} active={filterEstado === 'all'} />
                <StatCard title="Pendiente por aprobar" value={bandejaProcura.filter(r => r.estado === 'pendiente').length} icon={Hourglass} color="bg-yellow-500" onClick={() => setFilterEstado('pendiente')} active={filterEstado === 'pendiente'} />
                <StatCard title="En proceso" value={bandejaProcura.filter(r => r.estado === 'aprobada').length} icon={Clock} color="bg-amber-500" onClick={() => setFilterEstado('en_proceso')} active={filterEstado === 'en_proceso'} />
                <StatCard title="En gestión" value={bandejaProcura.filter(r => r.estado === 'en_gestion').length} icon={PlayCircle} color="bg-gray-500" onClick={() => setFilterEstado('en_gestion')} active={filterEstado === 'en_gestion'} />
                <StatCard title="Entregadas" value={bandejaProcura.filter(r => ['completada', 'recibido'].includes(r.estado)).length} icon={CheckCircle2} color="bg-emerald-500" onClick={() => setFilterEstado('completada')} active={filterEstado === 'completada'} />
            </div>

            <h2 className="text-lg font-bold text-gray-900">Bandeja de Procura {filterEstado !== 'all' && <span className="text-sm font-normal text-sura-blue ml-2">(Filtrado)</span>}</h2>

            {loading ? (
                <div className="flex justify-center py-12"><Loader2 size={24} className="animate-spin text-gray-400" /></div>
            ) : filteredBandejaProcura.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                    <CheckCircle size={48} className="mx-auto mb-3 opacity-30" />
                    <p>No hay requisiciones para mostrar</p>
                </div>
            ) : (
                <div className="space-y-3">{filteredBandejaProcura.map(renderRequisicionCard)}</div>
            )}
        </motion.div>
    );

    
    // ============================================
    // RENDER: STATS (DASHBOARD CORPORATIVO)
    // ============================================
    const renderStats = () => {
        if (statsLoading || !stats) {
            return <div className="flex justify-center py-20"><Loader2 size={32} className="animate-spin text-sura-blue" /></div>;
        }

        const COLORES_ESTADO = {
            pendiente: '#eab308',
            en_proceso: '#f59e0b',
            en_gestion: '#6b7280',
            completada: '#10b981',
            rechazada: '#ef4444'
        };
        const LABELS_ESTADO = {
            pendiente: 'Pendiente por aprobar',
            en_proceso: 'En proceso',
            en_gestion: 'En Gestión',
            completada: 'Entregadas',
            rechazada: 'Rechazadas'
        };

        const ccData = stats.stats_cc || [];
        const stackedData = ccData.map(cc => {
            const data = { name: cc.centro_costo.length > 15 ? cc.centro_costo.substring(0, 15) + '...' : cc.centro_costo, fullName: cc.centro_costo };
            const statusCounts = cc.estados;
            data.en_proceso = statusCounts.aprobada || 0;
            data.en_gestion = statusCounts.en_gestion || 0;
            data.completada = statusCounts.completada || 0;
            return data;
        });

        const estadoData = [
            { name: LABELS_ESTADO.en_proceso, value: stats.por_estado.aprobada || 0, color: COLORES_ESTADO.en_proceso, rawKey: 'en_proceso' },
            { name: LABELS_ESTADO.en_gestion, value: stats.por_estado.en_gestion || 0, color: COLORES_ESTADO.en_gestion, rawKey: 'en_gestion' },
            { name: LABELS_ESTADO.completada, value: stats.por_estado.completada || 0, color: COLORES_ESTADO.completada, rawKey: 'completada' },
            { name: LABELS_ESTADO.rechazada, value: stats.por_estado.rechazada || 0, color: COLORES_ESTADO.rechazada, rawKey: 'rechazada' },
        ].filter(e => e.value > 0);

        const porDia = stats.por_dia || [];
        const tendenciaData = (stats.tendencia || []).map(t => ({ label: t.mes, total: t.total }));
        const topSolicitantes = stats.top_solicitantes || [];

        return (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
                <div className="flex justify-between items-center bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                    <h2 className="text-lg font-bold text-sura-blue-dark flex items-center gap-2">
                        <BarChart2 size={20} /> Dashboard Operativo
                    </h2>
                    <select value={rangoStats} onChange={(e) => setRangoStats(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm font-medium focus:ring-2 focus:ring-sura-blue/20 outline-none">
                        <option value="all">Todo el tiempo</option>
                        <option value="mes_actual">Mes Actual</option>
                        <option value="mes_anterior">Mes Anterior</option>
                        <option value="ultimos_3_meses">Últimos 3 Meses</option>
                    </select>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    {[
                        { label: 'Total', value: stats.total - (stats.por_estado.pendiente || 0), color: 'text-sura-blue', bg: 'bg-blue-50', border: 'border-blue-100', icon: ClipboardList },
                        { label: 'En proceso', value: stats.por_estado.aprobada || 0, color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-100', icon: Clock },
                        { label: 'En Gestión', value: stats.por_estado.en_gestion || 0, color: 'text-gray-600', bg: 'bg-gray-100', border: 'border-gray-200', icon: PlayCircle },
                        { label: 'Entregadas', value: stats.por_estado.completada || 0, color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-100', icon: CheckCircle2 },
                        { label: 'Promedio de Cierre', value: stats.tiempo_promedio_dias ? `${stats.tiempo_promedio_dias} días` : '—', color: 'text-sura-blue', bg: 'bg-blue-50', border: 'border-blue-100', icon: Building },
                    ].map(kpi => (
                        <div key={kpi.label} className={`bg-white rounded-lg border border-gray-200 p-4 shadow-sm flex flex-col justify-between`}>
                            <div className="flex justify-between items-start mb-2">
                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{kpi.label}</p>
                                <div className={`${kpi.bg} ${kpi.border} border p-1.5 rounded-md flex-shrink-0`}><kpi.icon size={16} className={kpi.color} /></div>
                            </div>
                            <p className={`text-3xl font-bold ${kpi.color}`}>{kpi.value}</p>
                        </div>
                    ))}
                </div>

                {ccData.length > 0 && (
                    <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
                        <h3 className="text-sm font-bold text-sura-blue uppercase tracking-wide mb-6 flex items-center gap-2"><BarChart2 size={16} /> Distribución por Centro de Costo</h3>
                        <ResponsiveContainer width="100%" height={Math.max(280, stackedData.length * 42)}>
                            <BarChart data={stackedData} layout="vertical" barSize={18} margin={{ left: 10, right: 30 }} style={{ outline: 'none' }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
                                <XAxis type="number" tick={{ fontSize: 11, fill: '#6b7280' }} allowDecimals={false} />
                                <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#374151', fontWeight: 500 }} width={160} />
                                <Legend formatter={v => <span className="text-xs font-medium text-gray-600 ml-1">{LABELS_ESTADO[v] || v}</span>} iconType="square" wrapperStyle={{ paddingTop: '10px' }} />
                                {['en_proceso', 'en_gestion', 'completada'].map(estado => (
                                    <Bar key={estado} dataKey={estado} stackId="a" fill={COLORES_ESTADO[estado]} cursor="pointer" activeBar={false} onClick={(data) => {
                                        if (data && data.payload) {
                                            const estadosValidos = ['en_proceso', 'en_gestion', 'completada'].filter(k => data.payload[k] > 0);
                                            setDetalleGrafica({
                                                titulo: 'Centro de Costo: ' + data.payload.fullName,
                                                datos: estadosValidos.map(k => ({ label: LABELS_ESTADO[k] || k, value: data.payload[k], color: COLORES_ESTADO[k] }))
                                            });
                                        }
                                    }} />
                                ))}
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
                        <h3 className="text-sm font-bold text-sura-blue uppercase tracking-wide mb-6">Estado Global</h3>
                        {estadoData.length === 0 ? <div className="flex items-center justify-center h-48 text-gray-400 text-sm italic">Sin datos</div> : (
                            <ResponsiveContainer width="100%" height={240}>
                                <PieChart style={{ outline: 'none' }}>
                                    <Pie data={estadoData} dataKey="value" nameKey="name" cursor="pointer" activeShape={false} onClick={(data) => {
                                        if(data && data.name) {
                                            setDetalleGrafica({
                                                titulo: 'Estado Global: ' + data.name,
                                                datos: [{ label: data.name, value: data.value, color: data.payload.color }]
                                            });
                                        }
                                    }} cx="50%" cy="50%" outerRadius={90} innerRadius={55} paddingAngle={2}>
                                        {estadoData.map(entry => <Cell key={entry.name} fill={entry.color} />)}
                                    </Pie>
                                    <Legend formatter={v => <span className="text-xs font-medium text-gray-600 ml-1">{v}</span>} iconType="circle" />
                                </PieChart>
                            </ResponsiveContainer>
                        )}
                    </div>

                    <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
                        <h3 className="text-sm font-bold text-sura-blue uppercase tracking-wide mb-6">Actividad por Día</h3>
                        {porDia.every(d => d.total === 0) ? <div className="flex items-center justify-center h-48 text-gray-400 text-sm italic">Sin datos</div> : (
                            <ResponsiveContainer width="100%" height={240}>
                                <BarChart data={porDia} barSize={28} style={{ outline: 'none' }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                                    <XAxis dataKey="dia" tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} />
                                    <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} allowDecimals={false} axisLine={false} tickLine={false} />
                                    <Bar dataKey="total" fill="#003366" radius={[2, 2, 0, 0]} cursor="pointer" activeBar={false} onClick={(data) => {
                                        if(data && data.dia) {
                                            setDetalleGrafica({
                                                titulo: 'Actividad por día: ' + data.dia,
                                                datos: [{ label: 'Requisiciones', value: data.total, color: '#003366' }]
                                            });
                                        }
                                    }}>
                                        {porDia.map((d, i) => <Cell key={d.dia} fill={d.total === Math.max(...porDia.map(x => x.total)) ? '#003366' : '#8AC8FA'} />)}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm flex flex-col">
                        <h3 className="text-sm font-bold text-sura-blue uppercase tracking-wide mb-6">Tendencia Histórica</h3>
                        {tendenciaData.length <= 1 ? <div className="flex-1 flex items-center justify-center text-gray-400 text-sm italic min-h-[200px]">Data insuficiente</div> : (
                            <div className="flex-1">
                                <ResponsiveContainer width="100%" height={240}>
                                    <AreaChart data={tendenciaData} style={{ outline: 'none' }}>
                                        <defs>
                                            <linearGradient id="gradBlue" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#003366" stopOpacity={0.2} />
                                                <stop offset="95%" stopColor="#003366" stopOpacity={0} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                                        <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} />
                                        <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} allowDecimals={false} axisLine={false} tickLine={false} />
                                        <Area type="monotone" dataKey="total" stroke="#003366" strokeWidth={3} fill="url(#gradBlue)"
                                            dot={{ fill: '#ffffff', stroke: '#003366', r: 4, strokeWidth: 2 }}
                                            activeDot={{ 
                                                r: 8, fill: '#FFD100', stroke: '#003366', strokeWidth: 2, cursor: 'pointer',
                                                onClick: (e, payload) => {
                                                    if(payload && payload.payload) {
                                                        setDetalleGrafica({
                                                            titulo: 'Tendencia: ' + payload.payload.label,
                                                            datos: [{ label: 'Total', value: payload.payload.total, color: '#003366' }]
                                                        });
                                                    }
                                                }
                                            }} />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                        )}
                    </div>

                    <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
                        <h3 className="text-sm font-bold text-sura-blue uppercase tracking-wide mb-6">Top Solicitantes</h3>
                        {topSolicitantes.length === 0 ? <div className="flex items-center justify-center h-48 text-gray-400 text-sm italic">Sin datos</div> : (
                            <div className="space-y-4">
                                {topSolicitantes.map((s, i) => {
                                    const pct = Math.round((s.total / (topSolicitantes[0]?.total || 1)) * 100);
                                    return (
                                        <div key={s.nombre} className="flex items-center gap-4">
                                            <div className={`w-6 h-6 rounded flex items-center justify-center text-xs font-bold ${i < 3 ? 'bg-sura-blue text-white' : 'bg-gray-100 text-gray-500'}`}>{i + 1}</div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center justify-between mb-1.5">
                                                    <span className="text-sm font-medium text-gray-800 truncate">{s.nombre}</span>
                                                    <span className="text-sm font-bold text-gray-900 bg-gray-100 px-2 py-0.5 rounded">{s.total}</span>
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    <div className="flex-1 bg-gray-100 rounded-sm h-1.5 overflow-hidden">
                                                        <div className="h-full transition-all bg-sura-blue" style={{ width: `${pct}%` }} />
                                                    </div>
                                                    <span className="text-xs text-gray-500 flex-shrink-0 w-24 truncate text-right">{s.centro_costo}</span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </motion.div>
        );
    };


    // ============================================
    // RENDER PRINCIPAL
    // ============================================
    return (
        <div className="max-w-6xl mx-auto">
            {errorCarga && (
                <div className="mb-4 flex items-start gap-2 bg-red-50 border border-red-100 text-red-700 px-4 py-3 rounded-xl text-sm">
                    <XCircle size={16} className="mt-0.5 shrink-0" />
                    <span>{errorCarga}</span>
                </div>
            )}
            {isProcura && !['create', 'detail', 'edit'].includes(viewMode) && (
                <div className="flex gap-1 mb-6 bg-gray-100 p-1 rounded-xl w-fit">
                    <button onClick={() => setViewMode('procura')}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${viewMode === 'procura' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                        Bandeja Procura
                    </button>
                    <button onClick={() => setViewMode('list')}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${viewMode === 'list' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                        Mis requisiciones
                    </button>
                    <button onClick={() => setViewMode('stats')}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${viewMode === 'stats' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                        <span className="flex items-center gap-1.5"><BarChart2 size={13} />Estadísticas</span>
                    </button>
                </div>
            )}

            <AnimatePresence mode="wait">
                {viewMode === 'list' && renderList()}
                {viewMode === 'create' && renderForm(false)}
                {viewMode === 'edit' && renderForm(true)}
                {viewMode === 'detail' && renderDetail()}
                {viewMode === 'procura' && renderProcura()}
                {viewMode === 'stats' && renderStats()}
            </AnimatePresence>

            {/* Modal rechazo */}
            {showRechazoModal && (
                <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowRechazoModal(false)}>
                    <div className="bg-white rounded-2xl p-6 w-full max-w-md space-y-4" onClick={e => e.stopPropagation()}>
                        <h3 className="text-lg font-bold text-gray-900">Rechazar requisición</h3>
                        <textarea value={motivoRechazo} onChange={e => setMotivoRechazo(e.target.value)}
                            placeholder="Motivo del rechazo..." rows={3}
                            className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-100 resize-none" autoFocus />
                        <div className="flex justify-end gap-2">
                            <button onClick={() => { setShowRechazoModal(false); setMotivoRechazo(''); }} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancelar</button>
                            <button onClick={handleRechazar} disabled={submitting || !motivoRechazo.trim()}
                                className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">
                                {submitting ? 'Rechazando...' : 'Rechazar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal completar */}
            {showCompletarModal && (
                <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowCompletarModal(false)}>
                    <div className="bg-white rounded-2xl p-6 w-full max-w-md space-y-4" onClick={e => e.stopPropagation()}>
                        <h3 className="text-lg font-bold text-gray-900">Completar requisición</h3>
                        <textarea value={notaProcura} onChange={e => setNotaProcura(e.target.value)}
                            placeholder="Nota de cierre (opcional)..." rows={3}
                            className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-100 resize-none" />
                        <div className="flex justify-end gap-2">
                            <button onClick={() => { setShowCompletarModal(false); setNotaProcura(''); }} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancelar</button>
                            <button onClick={handleCompletar} disabled={submitting}
                                className="px-4 py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50">
                                {submitting ? 'Completando...' : 'Completar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal detalle gráfica */}
            {detalleGrafica && createPortal(
                <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-[9999]" onClick={() => setDetalleGrafica(null)} style={{ overflowY: 'auto' }}>
                    <div className="bg-white rounded-xl p-6 w-full max-w-sm shadow-2xl border border-gray-200 m-auto" onClick={e => e.stopPropagation()}>
                        <div className="flex justify-between items-start mb-4 border-b border-gray-100 pb-3">
                            <h3 className="text-lg font-bold text-[#003366]">{detalleGrafica.titulo}</h3>
                            <button onClick={() => setDetalleGrafica(null)} className="text-gray-400 hover:text-gray-600 transition-colors">
                                <X size={24} />
                            </button>
                        </div>
                        <div className="space-y-3">
                            {detalleGrafica.datos.map((d, i) => (
                                <div key={i} className="flex justify-between items-center bg-gray-50 p-4 rounded-lg border border-gray-100">
                                    <div className="flex items-center gap-3">
                                        <div className="w-4 h-4 rounded-sm shadow-sm" style={{ backgroundColor: d.color }}></div>
                                        <span className="text-sm font-semibold text-gray-700">{d.label}</span>
                                    </div>
                                    <span className="text-xl font-bold text-gray-900">{d.value}</span>
                                </div>
                            ))}
                        </div>
                        <div className="mt-6 text-right">
                            <button onClick={() => setDetalleGrafica(null)} className="w-full px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-bold rounded-lg transition-colors">
                                CERRAR DETALLE
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>

    );
};

export default RequisicionComprasView;
